import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { createEntityAdapter, EntityState } from "@ngrx/entity";
import { Action, createAction, createFeatureSelector, createReducer, createSelector, on, props } from "@ngrx/store";

import { merge } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { CANTONS_NODEID } from "src/consts";
import { PublicationNode } from "src/core/backend/controllers";
import { fromRouter } from "src/shared/store/selectors";

import { cacheClear$ } from "../shared/services/cache-clear.service";
import { HierarchyCacheService } from "../shared/services/hierarchy-cache.service";

interface _State extends EntityState<PublicationNode> {}

const _featureName = "publications";

const adapter = createEntityAdapter<PublicationNode>({
  selectId: (n) => n.id,
  sortComparer: (a, b) => (a.title || "").localeCompare(b.title || ""),
});

namespace _Action {
  export const Load = {
    request: createAction("[Publications] Load.Request"),
    success: createAction("[Publications] Load.Success", props<{ nodes: PublicationNode[] }>()),
  };
}

const initialState: _State = adapter.getInitialState();

const _reducer = createReducer(
  initialState,
  on(_Action.Load.success, (state, { nodes }) => adapter.setAll(nodes, state))
);

export namespace fromPublications {
  const selectFeature = createFeatureSelector<_State>(_featureName);

  export const { selectAll, selectEntities, selectIds, selectTotal } = adapter.getSelectors(selectFeature);

  export const selectValidatedOrgId = createSelector(
    selectAll,
    fromRouter.selectOrgId,
    (pubNodes, routeOrgId) => (pubNodes.some((p) => p.organization === routeOrgId) && routeOrgId) || CANTONS_NODEID
  );

  export const selectValidatedRoutePubType = createSelector(
    selectAll,
    fromRouter.selectPubType,
    selectValidatedOrgId,
    (pubNodes, routePubType, orgId) => pubNodes.find((p) => (!orgId || p.organization === orgId) && p.id === routePubType)?.id
  );

  export const selectUsingRouteOrgId = createSelector(selectAll, selectValidatedOrgId, (pubNodes, orgId) => {
    orgId = orgId || CANTONS_NODEID;
    return pubNodes.filter((p) => p.organization === orgId);
  });
}

/****************************************************************/

@Injectable()
export class PublicationsEffects {
  private readonly actions = inject(Actions);
  private readonly hierarchyCache = inject(HierarchyCacheService);

  readonly load$ = createEffect(() =>
    merge(cacheClear$, this.actions.pipe(ofType(_Action.Load.request))).pipe(
      switchMap(() => this.hierarchyCache.getPublicationNodes()),
      map((nodes) => PublicationsStore.Action.Load.success({ nodes: clone(nodes) }))
    )
  );
}

/****************************************************************/

export namespace PublicationsStore {
  export const featureName = _featureName;
  export interface State extends _State {}
  export import Action = _Action;
  export function reducer(state: State | undefined, action: Action) {
    return _reducer(state, action);
  }
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}
